import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import ThemeToggle from "@/components/ThemeToggle";
import { ArrowLeft, CreditCard, CheckCircle2, XCircle, Loader2, Zap, Star, Building2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGE_ICONS = { starter: Zap, business: Star, pro: Building2 };
const PACKAGE_COLORS = {
  starter: "border-green-500/30 bg-gradient-to-br from-green-500/10 to-emerald-500/5",
  business: "border-primary/40 bg-gradient-to-br from-primary/15 to-green-600/5 ring-1 ring-primary/20",
  pro: "border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-blue-600/5",
};

export default function BillingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(null);
  const [payStatus, setPayStatus] = useState(null); // success | cancelled | null

  const fetchPackages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/payments/packages`, { credentials: "include" });
      if (res.ok) setPackages(await res.json());
    } catch {}
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/payments/history`, { credentials: "include" });
      if (res.ok) setHistory(await res.json());
    } catch {}
  }, []);

  // Zpracování návratu ze Stripe
  useEffect(() => {
    const status = searchParams.get("status");
    const sessionId = searchParams.get("session_id");

    if (status === "cancelled") {
      setPayStatus("cancelled");
      toast.error("Platba byla zrušena.");
    } else if (sessionId) {
      setPayStatus("checking");
      pollStatus(sessionId);
    }

    fetchPackages();
    fetchHistory();
  }, []);

  const pollStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 8) {
      setPayStatus("timeout");
      return;
    }
    try {
      const res = await fetch(`${API}/payments/status/${sessionId}`, { credentials: "include" });
      const data = await res.json();
      if (data.payment_status === "paid") {
        setPayStatus("success");
        toast.success(`Platba proběhla úspěšně! Přidáno ${data.credits_added} kreditů.`);
        refreshUser();
        fetchHistory();
      } else if (data.payment_status === "expired") {
        setPayStatus("expired");
      } else {
        setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(sessionId, attempts + 1), 3000);
    }
  };

  const handleBuy = async (packageId) => {
    setLoading(packageId);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Chyba platby");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(null);
    }
  };

  const creditBalance = user?.credits === -1 ? "Neomezeno" : user?.credits ?? 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/chat")} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Zpět do chatu
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <MessageSquare className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>ChatGreen</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground">
            Kredity: <span className="font-semibold text-foreground">{creditBalance}</span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Payment status banner */}
        {payStatus === "success" && (
          <div className="mb-8 flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-xl px-5 py-4 fade-in">
            <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
            <p className="text-sm font-medium">Platba proběhla úspěšně! Kredity byly přidány na váš účet.</p>
          </div>
        )}
        {payStatus === "cancelled" && (
          <div className="mb-8 flex items-center gap-3 bg-destructive/10 border border-destructive/30 rounded-xl px-5 py-4 fade-in">
            <XCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            <p className="text-sm font-medium">Platba byla zrušena. Nic nebylo účtováno.</p>
          </div>
        )}
        {payStatus === "checking" && (
          <div className="mb-8 flex items-center gap-3 bg-primary/10 border border-primary/30 rounded-xl px-5 py-4 fade-in">
            <Loader2 className="h-5 w-5 text-primary flex-shrink-0 animate-spin" />
            <p className="text-sm font-medium">Ověřuji platbu...</p>
          </div>
        )}

        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Dobijte kredity
          </h1>
          <p className="text-muted-foreground text-lg">
            Každá zpráva = 1 kredit. Platíte jen co skutečně využijete.
          </p>
        </div>

        {/* Packages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {packages.map((pkg) => {
            const Icon = PACKAGE_ICONS[pkg.id] || Zap;
            const isPopular = pkg.id === "business";
            const pricePerCredit = (pkg.amount / pkg.credits).toFixed(2);
            return (
              <Card
                key={pkg.id}
                className={`relative border ${PACKAGE_COLORS[pkg.id]} transition-all hover:-translate-y-1 hover:shadow-lg`}
                data-testid={`package-${pkg.id}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground text-xs px-3">Nejoblíbenější</Badge>
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>{pkg.name}</h3>
                      <p className="text-xs text-muted-foreground">{pkg.desc}</p>
                    </div>
                  </div>

                  <div className="mb-5">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {pkg.amount.toLocaleString("cs-CZ")}
                      </span>
                      <span className="text-lg text-muted-foreground">Kč</span>
                    </div>
                    <p className="text-sm text-primary font-medium mt-1">
                      {pkg.credits.toLocaleString("cs-CZ")} kreditů
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {pricePerCredit} Kč / kredit
                    </p>
                  </div>

                  <Button
                    className="w-full bg-primary hover:bg-primary/90 gap-2"
                    onClick={() => handleBuy(pkg.id)}
                    disabled={loading === pkg.id}
                    data-testid={`buy-${pkg.id}`}
                  >
                    {loading === pkg.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4" />
                    )}
                    Koupit nyní
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Platební história */}
        {history.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
              Historie plateb
            </h2>
            <Card className="border-border">
              <div className="divide-y divide-border">
                {history.map((tx) => (
                  <div key={tx.transaction_id} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">{CREDIT_PACKAGES_CZ[tx.package_id] || tx.package_id}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(tx.created_at).toLocaleDateString("cs-CZ", { day: "2-digit", month: "2-digit", year: "numeric" })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-primary">+{tx.credits} kreditů</p>
                      <Badge
                        variant={tx.payment_status === "paid" ? "outline" : "secondary"}
                        className="text-xs"
                      >
                        {tx.payment_status === "paid" ? "Zaplaceno" : tx.payment_status === "pending" ? "Čeká" : tx.payment_status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Info */}
        <div className="mt-10 text-center">
          <p className="text-xs text-muted-foreground">
            Platby jsou zabezpečeny přes Stripe. Přijímáme karty Visa, Mastercard a další.
            <br />
            Při problémech s platbou kontaktujte podporu.
          </p>
        </div>
      </div>
    </div>
  );
}

// Pomocný mapping pro historii
const CREDIT_PACKAGES_CZ = {
  starter: "Starter – 100 kreditů",
  business: "Business – 500 kreditů",
  pro: "Pro – 2000 kreditů",
};
